import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { badgeClass, Field, formatMoney, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface PastVersion {
  revision: number;
  grandTotal: number;
  sentAt: string | null;
  sentTo: string | null;
  revisedBecause: string;
  revisedBy: string | null;
  revisedAt: string;
  changes: string[];
}
interface QuoteDocument {
  id: string;
  orderNumber: string;
  label: string;
  revision: number;
  sentAt: string | null;
  sentTo: string | null;
  validUntil: string | null;
  grandTotal: number;
  history: PastVersion[];
}

const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const quoteDocumentApi = {
  get: async (id: string): Promise<QuoteDocument> => (await api.get(`/sales/quotes/${id}/revisions`)).data,
  markSent: async (id: string, sentTo: string): Promise<QuoteDocument> => (await api.post(`/sales/quotes/${id}/mark-sent`, { sentTo: sentTo || null })).data,
  email: async (id: string, data: object): Promise<QuoteDocument> => (await api.post(`/sales/quotes/${id}/email`, data)).data,
  // The PDF is fetched with the sign-in and handed to the browser as a download
  download: async (id: string, fallbackName: string): Promise<void> => {
    const response = await api.get(`/sales/quotes/${id}/pdf`, { responseType: 'blob' });
    const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = named?.[1] ?? fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

// A failed download comes back as a blob; its message is inside
const blobMessage = async (error: unknown, fallback: string): Promise<string> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return getApiErrorMessage(error, fallback);
};

interface QuoteDocumentPanelProps {
  quoteId: string;
  customerEmail?: string | null;
  canSend: boolean;
}

// The quotation as a document: which revision it is, whether it has gone to the customer, its
// PDF, sending it, and the versions before this one
const QuoteDocumentPanel: React.FC<QuoteDocumentPanelProps> = ({ quoteId, customerEmail, canSend }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['quotes', 'document', quoteId], queryFn: () => quoteDocumentApi.get(quoteId) });
  const [mode, setMode] = useState<'idle' | 'mark' | 'email'>('idle');
  const [sentTo, setSentTo] = useState('');
  const [email, setEmail] = useState({ to: customerEmail ?? '', subject: '', message: '' });
  const [emailError, setEmailError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const done = (message: string) => (saved: QuoteDocument) => {
    toast.success(message);
    queryClient.setQueryData(['quotes', 'document', quoteId], saved);
    // The list shows whether a quotation has been sent
    queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
    setMode('idle');
  };
  const markSent = useMutation({ mutationFn: () => quoteDocumentApi.markSent(quoteId, sentTo.trim()), onSuccess: done('Quotation marked as sent'), onError: (error) => toast.error(getApiErrorMessage(error, 'That could not be recorded')) });
  const sendEmail = useMutation({
    mutationFn: () => quoteDocumentApi.email(quoteId, { to: email.to.trim(), subject: email.subject.trim() || undefined, message: email.message.trim() || undefined }),
    onSuccess: done(`Quotation emailed to ${email.to.trim()}`),
    onError: (error) => toast.error(getApiErrorMessage(error, 'The email could not be sent'), { duration: 8000 }),
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await quoteDocumentApi.download(quoteId, `${data?.label ?? 'quotation'}.pdf`);
    } catch (error) {
      toast.error(await blobMessage(error, 'The PDF could not be made'), { duration: 8000 });
    } finally {
      setDownloading(false);
    }
  };
  const handleEmail = (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.to.trim())) {
      setEmailError('Enter a valid email address');
      return;
    }
    setEmailError('');
    sendEmail.mutate();
  };

  if (isLoading || !data) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <section className="rounded-lg border border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900">{data.revision > 0 ? `Revision ${data.revision}` : 'First version'}</span>
          {data.sentAt ? (
            <>
              <span className={cn(badgeClass, 'ml-2 bg-green-50 text-green-700 ring-green-600/20')}>Sent</span>
              <span className="ml-2">
                {formatDate(data.sentAt)}
                {data.sentTo ? ` to ${data.sentTo}` : ''}
              </span>
            </>
          ) : (
            <span className={cn(badgeClass, 'ml-2 bg-gray-50 text-gray-700 ring-gray-500/20')}>Not sent yet</span>
          )}
          <span className="ml-3 text-gray-500">Valid until {formatDate(data.validUntil)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleDownload} disabled={downloading} className={secondaryButtonClass}>
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            {downloading ? 'Making the PDF…' : 'Download PDF'}
          </button>
          {canSend && (
            <>
              <button type="button" onClick={() => setMode(mode === 'mark' ? 'idle' : 'mark')} className={secondaryButtonClass}>
                <PaperAirplaneIcon className="mr-2 h-4 w-4" />
                Mark as Sent
              </button>
              <button type="button" onClick={() => setMode(mode === 'email' ? 'idle' : 'email')} className={secondaryButtonClass}>
                <EnvelopeIcon className="mr-2 h-4 w-4" />
                Email
              </button>
            </>
          )}
        </div>
      </div>

      {mode === 'mark' && (
        <div className="space-y-3 border-b border-gray-200 px-4 py-3">
          <p className="text-sm text-gray-600">For a quotation that went by hand, post or your own email. Once it is marked as sent, changing it makes a new revision and keeps this version.</p>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Sent to" htmlFor="qd-sent-to" hint="Optional" className="min-w-64 flex-1">
              <input id="qd-sent-to" type="text" maxLength={200} value={sentTo} onChange={(event) => setSentTo(event.target.value)} placeholder={customerEmail ?? ''} className={inputClass} />
            </Field>
            <button type="button" onClick={() => markSent.mutate()} disabled={markSent.isPending} className={primaryButtonClass}>
              {markSent.isPending ? 'Saving…' : 'Confirm Sent'}
            </button>
          </div>
        </div>
      )}

      {mode === 'email' && (
        <form onSubmit={handleEmail} noValidate className="space-y-3 border-b border-gray-200 px-4 py-3">
          <p className="text-sm text-gray-600">The quotation goes as a PDF attached to this email, and is then marked as sent.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="To" htmlFor="qd-to" required error={emailError}>
              <input id="qd-to" type="email" value={email.to} onChange={(event) => setEmail((prev) => ({ ...prev, to: event.target.value }))} className={cn(inputClass, emailError && inputErrorClass)} />
            </Field>
            <Field label="Subject" htmlFor="qd-subject" hint="Left empty: Quotation, its number and your company name">
              <input id="qd-subject" type="text" maxLength={200} value={email.subject} onChange={(event) => setEmail((prev) => ({ ...prev, subject: event.target.value }))} className={inputClass} />
            </Field>
            <Field label="Covering note" htmlFor="qd-message" className="sm:col-span-2">
              <textarea id="qd-message" rows={3} maxLength={4000} value={email.message} onChange={(event) => setEmail((prev) => ({ ...prev, message: event.target.value }))} className={inputClass} placeholder={`Please find attached our quotation ${data.label} for ${formatMoney(data.grandTotal)}.`} />
            </Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={sendEmail.isPending} className={primaryButtonClass}>
              {sendEmail.isPending ? 'Sending…' : 'Send Email'}
            </button>
          </div>
        </form>
      )}

      <div className="px-4 py-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Earlier versions · {data.history.length}</h4>
        {data.history.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500">None. A change made after the quotation has been sent keeps the sent version here.</p>
        ) : (
          <ul className="mt-2 space-y-3">
            {data.history.map((version) => (
              <li key={version.revision} className="text-sm">
                <p className="text-gray-900">
                  <span className="font-semibold">{version.revision > 0 ? `Revision ${version.revision}` : 'First version'}</span> · {formatMoney(version.grandTotal)}
                  <span className="text-gray-500">
                    {' '}
                    · sent {formatDate(version.sentAt)}
                    {version.sentTo ? ` to ${version.sentTo}` : ''}
                  </span>
                </p>
                <p className="mt-0.5 text-gray-700">
                  Revised {formatDate(version.revisedAt)}
                  {version.revisedBy ? ` by ${version.revisedBy}` : ''}: {version.revisedBecause}
                </p>
                {version.changes.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-gray-600">
                    {version.changes.map((change) => (
                      <li key={change}>{change}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default QuoteDocumentPanel;
